import React, { useEffect, useState } from "react";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL } from "src/constants/constants";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Column, Row, Text, Icon, Card, Loader } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { getInspectionWorkflow } from "src/store/workflowSlice";
import InspectionService, {
    InspectionData,
    InspectionResult,
} from "src/services/inspectionService";
import { resetInspectionCaptureState } from "src/store/inspectionCaptureSlice";
import { inspectionFlowTimingLogger } from "src/services/inspectionFlowTimingLogger";
import CheckpointList from "./CheckpointList";
import { formatDate } from "src/utils/dateUtil";
import { isResultNA } from "src/components/pages/inspection/utils";
import { orderGroupedResultsByQuestion, isAnalysisPending } from "../utils/resultGrouping";
import "./inspectionReviewPage.scss";
import ThemedButton from "./ThemedButton";

const POLL_INTERVAL_MS = 3000;

/** Check if any result across all groups is still pending */
const hasAnyPendingAnalysis = (
	groupedResults: Record<string, InspectionResult[]>
): boolean =>
	Object.values(groupedResults).some((results) =>
		results.some(isAnalysisPending)
	);

/** Determine overall compliance from grouped results */
const getOverallCompliance = (
    groupedResults: Record<string, InspectionResult[]>
): boolean => {
    return Object.values(groupedResults).every((results) => {
        const scoredResults = results.filter((r) => !isResultNA(r));
        return scoredResults.length === 0 || scoredResults.every((r) => r.isCompliant);
    }
    );
};

const InspectionReviewPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    // Optional deep link (?question=<id>) used by the SafetyGenie result links we
    // send to Austin, so an Austin reviewer lands on the specific question.
    const targetQuestionId = searchParams.get("question");
    const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
    const [presignedUrlMap, setPresignedUrlMap] = useState<Record<string, string>>({});
    const [groupedResults, setGroupedResults] = useState<Record<string, InspectionResult[]>>({});
    const [hasPendingResults, setHasPendingResults] = useState(true);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { questions } = useAppSelector((state) => state.workflow);

    // Fetch workflow config if not already loaded
    useEffect(() => {
        if (!questions || questions.length === 0) {
            dispatch(getInspectionWorkflow());
        }
    }, [questions]);

		// Fetch inspection data on mount, then poll while any result has pending AI analysis
		useEffect(() => {
			if(!hasPendingResults) return;
	
			const fetchInspection = async () => {
				try {
					const data = await InspectionService.fetchInspectionWithPresignedUrls(
						inspectionId!
					);
					setInspectionData(data.inspectionData);
					setPresignedUrlMap(data.presignedUrlMap);
					setGroupedResults(data.groupedResults);
					const stillPending = hasAnyPendingAnalysis(data.groupedResults);
					setHasPendingResults(stillPending);
					// Identifies this inspection for a resumed draft landing directly
					// here (no prior startInspection() call); no-op if already set.
					inspectionFlowTimingLogger.identifyInspection(inspectionId!, data.inspectionData.siteCode);
					if (!stillPending) {
						// Results are no longer pending analysis: the inspector can now
						// actually read/override them. Marks the start of the review
						// phase (a no-op if already marked, e.g. re-fetch after a
						// transient error).
						inspectionFlowTimingLogger.markReviewStart();
					}
				} catch (err: any) {
					console.error("Failed to fetch inspection data:", err);
					setHasPendingResults(false);
				}
			};

			fetchInspection();
			const intervalId = setInterval(fetchInspection, POLL_INTERVAL_MS);
			return () => clearInterval(intervalId);
		}, [inspectionId, hasPendingResults]);

    const submitInspection = async () => {
        // Guard against double submits while a request is already in flight.
        if (isSubmitting) return;

        setIsSubmitting(true);
        // Closes out the review phase and starts timing the Austin/SafetyGenie
        // submission call itself.
        inspectionFlowTimingLogger.markSubmissionStart();
        try {
            // submit_inspection handles both cases: it submits to Austin/RCMS when the
            // inspection is linked to a scheduled Austin inspection, and simply completes
            // it in SafetyGenie otherwise.
            await InspectionService.submitInspection(inspectionId!);
            // Submission succeeded: this is the end of the whole timed flow
            // (Start Inspection click -> submission complete). Logs + emits
            // metrics, then resets for the next inspection.
            inspectionFlowTimingLogger.recordInspectionComplete();
            dispatch(resetInspectionCaptureState());
            navigate(`/configurationDrivenInspections/${inspectionId}/submitted`, {
                state: {
                    overallCompliant: getOverallCompliance(groupedResults),
                    isAustinLinked: Boolean(inspectionData?.austinInspectionId),
                },
            });
        } catch (error) {
            console.error("Failed to submit inspection:", error);
            // Re-enable the button only on failure; on success we navigate away.
            setIsSubmitting(false);
        }
    };

    // Scroll to and briefly highlight the deep-linked question once results are rendered.
    useEffect(() => {
        if (!targetQuestionId || hasPendingResults || !inspectionData) return;
        const el = document.getElementById(`question-${targetQuestionId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedQuestionId(targetQuestionId);
    }, [targetQuestionId, hasPendingResults, inspectionData]);

    const getQuestionText = (questionId: string): string => {
        const question = questions.find((q) => q.id === questionId);
        return question?.text || questionId;
    };

    if (!inspectionData) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Loading inspection data...</Text>
            </Column>
        );
    }

    const isCompleted = inspectionData.status.toLowerCase() === "completed";

    const overallCompliant = getOverallCompliance(groupedResults);
    const { datePart, timePart } = formatDate(inspectionData.createdAt);
    const questionEntries = orderGroupedResultsByQuestion(groupedResults, questions);

    return (
        <div className="inspection-review-page pwa-scrollable">
            <Column spacingInset="500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="inspection-review-back-button"
                        onClick={() => {
                            dispatch(resetInspectionCaptureState());
                            navigate("/");
                        }}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="primary" />
                    </div>
                    <Text type="b300" color="primary">
                        Inspection Review
                    </Text>
                </Row>

                <Text type="h300" color="primary">
                    {inspectionData?.templateName ?? "Inspection"}
                </Text>

                <Card spacingInset="none">
                    <Column spacingInset="400" spacing="200">
                        <Row spacing="200">
                            <Text type="b200" color="primary">
                                {datePart}
                            </Text>
                            <Text type="b200" color="secondary">
                                {timePart}
                            </Text>
                        </Row>
                        <Text type="b100" color="primary">
                            OBSERVATION
                        </Text>
                        {hasPendingResults ? (
							<Row spacing="200" alignmentVertical="center" alignmentHorizontal="justify">
								<Text type="h300" color="secondary">
									Analyzing...
								</Text>
								<Loader size="small" type="circular" />
							</Row>
						) : (
							<Text
								type="h300"
								color={overallCompliant ? "success" : "error"}
							>
								{overallCompliant ? COMPLIANT_LABEL : NON_COMPLIANT_LABEL}
							</Text>
						)}
                    </Column>
                </Card>

                <Text type="h200" color="primary">
                    Check points
                </Text>

                <CheckpointList
                    questionEntries={questionEntries}
                    questions={questions}
                    presignedUrlMap={presignedUrlMap}
                    highlightedQuestionId={highlightedQuestionId}
                    override={
                        !isCompleted
                            ? {
                                onResult: (result, questionId) =>
                                    navigate(`/configurationDrivenInspections/${inspectionId}/override`, {
                                        state: { result, questionText: getQuestionText(questionId), questionMode: questions.find((q) => q.id === questionId)?.mode },
                                    }),
                                onObjectCheck: (firstResult, questionId, check) => {
                                    const baseQuestion = getQuestionText(questionId);
                                    const displayName = check.object_name.replace(/\s*sign\s*$/i, "").trim();
                                    const questionWithSign = baseQuestion.replace("?", ` (${displayName})?`);
                                    navigate(`/configurationDrivenInspections/${inspectionId}/override`, {
                                        state: { result: firstResult, questionText: questionWithSign, objectCheck: check },
                                    });
                                },
                                onCount: (result, questionId) => {
                                    // Hazardous containers can't exceed the number of
                                    // containers actually inspected (distinct objectIds
                                    // across all results).
                                    const totalContainers = new Set(
                                        Object.values(groupedResults)
                                            .flat()
                                            .map((r) => r.objectId)
                                            .filter((id): id is string => !!id)
                                    ).size;
                                    navigate(`/configurationDrivenInspections/${inspectionId}/overrideCount`, {
                                        state: { result, questionText: getQuestionText(questionId), maxCount: totalContainers },
                                    });
                                },
                            }
                            : undefined
                    }
                />

                {hasPendingResults===false && inspectionData.status.toLowerCase() === "in_progress" && (
                    <ThemedButton
                        onClick={submitInspection} disabled={hasPendingResults || isSubmitting}
                    >
                        {isSubmitting
                            ? "Submitting..."
                            : inspectionData.austinInspectionId
                                ? "SUBMIT TO AUSTIN"
                                : "Submit Inspection"}
                    </ThemedButton>
                )}
            </Column>
        </div>
    );
};

export default InspectionReviewPage;
